import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {CommonModule, Location} from '@angular/common';
import {FormsModule, NgModel} from '@angular/forms';
import {PaymentDetails, PaymentLineItem} from '../../models/payment-details-model';
import {PacService} from '../../shared/services/pac.service';
import {
  AdditionalPACData,
  PacUpdateRequest,
  PaymentCategory,
  PaymentFrequency
} from '../../models/additional-pac-data.model';
import {VirtualKeyboardComponent} from '../../components/virtual-keyboard/virtual-keyboard.component';
import {ErrorBannerComponent} from '../../components/error-banner/error-banner.component';
import {AppConstants} from '../../shared/app-constants';
import {AuthService} from '../../shared/services/auth.service';
import {PaymentDetailsService} from '../../shared/services/payment-details.service';
import {take} from 'rxjs';

@Component({
  selector: 'app-confirm-payments-details',
  imports: [CommonModule, FormsModule, VirtualKeyboardComponent, ErrorBannerComponent],
  templateUrl: './confirm-payments-details.component.html',
  styleUrl: './confirm-payments-details.component.scss',
  standalone: true
})
export class ConfirmPaymentsDetailsComponent implements OnInit {
  payerData!: PaymentDetails;
  paymentLineItems: PaymentLineItem[] = [];
  pacNumber: string = '';
  payerId: string = '';
  selectedIndices = new Set<number>();
  emailId: string = '';
  contactNumber: string = '';
  errorMessage: string = '';
  paymentType: string | null = null;
  pacErrorMessage: string = '';

  checkboxStates: { checked: boolean; enabled: boolean }[] = [];

  constructor(private readonly router: Router,
              private readonly route: ActivatedRoute,
              private readonly location: Location,
              private readonly pacService: PacService,
              private readonly authService: AuthService,
              private readonly paymentDetailsService: PaymentDetailsService) {
    this.loadData();
  }

  ngOnInit(): void {
    const paymentRef = this.route.snapshot.queryParamMap.get('paymentReference');
    const payerId = this.route.snapshot.queryParamMap.get('id');
    if (paymentRef && payerId) {
      this.pacNumber = paymentRef;
      this.payerId = payerId;
      this.authService.fetchAuthToken().pipe(take(1)).subscribe({
        next: (token) => {
          this.authService.setToken(token);
          this.fetchDetails();
        },
        error: () => { // NOSONAR
          // intentionally ignored or handled globally
        }
      });
    }
  }

  fetchDetails() {
    this.paymentDetailsService.fetchDetails(this.pacNumber, this.payerId).subscribe({
      next: (response: any) => {
        const errorMsg = response?.fetchPaymentInfoReturn?.errorMessage || response?.errorMessage;
        if (errorMsg) {
          const isConnectionRefused = errorMsg.includes('Connection refused');
          // Handled Error when CBS is unavailable
          if (isConnectionRefused) {
            this.pacErrorMessage = AppConstants.ERROR_SERVICE_UNAVAILABLE;
          }

          // Set generic message only if it's NOT an invalid PAC or connection error
          if(!isConnectionRefused){
            this.pacErrorMessage = errorMsg;
          }
          return; // Don't proceed to load data if there's an error
        }
        // No error: proceed to load data
        this.loadDataFromAPI(response);
      },
      error: (err) => {
        const connectionError = err?.error?.error?.message || err?.error?.message || err?.message || '';
        // Handled Error when Konglet is unavailable
        if (connectionError.includes('Connection refused')) {
          this.pacErrorMessage = AppConstants.ERROR_SERVICE_UNAVAILABLE;
        }
      }
    });
  }


  loadData() {
    const nav = this.router.getCurrentNavigation();
    const paymentDetails = nav?.extras?.state?.['pacDetails'];
    this.pacNumber = paymentDetails?.fetchPaymentInfoReturn?.wspaymentAdvice?.paymentReferenceNumber;

    if (paymentDetails?.fetchPaymentInfoReturn?.wspaymentAdvice) {
      this.payerData = paymentDetails.fetchPaymentInfoReturn.wspaymentAdvice;
      this.paymentLineItems = this.payerData.wsPaymentLine?.wsPaymentLine || [];

      this.checkboxStates = this.paymentLineItems.map((_, index) => ({
        checked: index === 0,
        enabled: index === 0
      }));

      if (this.paymentLineItems.length > 0) {
        this.selectedIndices.add(0);
        if (this.paymentLineItems.length > 1) {
          this.checkboxStates[1].enabled = true;
        }
      }

    }
  }

  loadDataFromAPI(apiResponse: any) {
    // Assuming the API returns the same structure as state
    if (apiResponse?.fetchPaymentInfoReturn?.wspaymentAdvice) {
      this.payerData = apiResponse.fetchPaymentInfoReturn?.wspaymentAdvice;
      this.pacNumber = apiResponse.fetchPaymentInfoReturn.wspaymentAdvice?.paymentReferenceNumber;
      this.setupLineItems(this.payerData);
    }
  }

  setupLineItems(data: any) {
    this.paymentLineItems = data.wsPaymentLine?.wsPaymentLine || [];

    this.checkboxStates = this.paymentLineItems.map((_, index) => ({
      checked: index === 0,
      enabled: index === 0
    }));

    if (this.paymentLineItems.length > 0) {
      this.selectedIndices = new Set([0]);
      if (this.paymentLineItems.length > 1) {
        this.checkboxStates[1].enabled = true;
      }
    }
  }

  onClick() {
    this.router.navigate(['']);
  }

  toggleSelection(index: number) {
    const current = this.checkboxStates[index];
    current.checked = !current.checked;

    if (current.checked) {
      this.selectedIndices.add(index);
      if (index + 1 < this.checkboxStates.length) {
        this.checkboxStates[index + 1].enabled = true;
      }
    } else {
      this.selectedIndices.delete(index);

      for (let i = index + 1; i < this.checkboxStates.length; i++) {
        this.checkboxStates[i].checked = false;
        this.selectedIndices.delete(i);
      }

      for (let i = 1; i < this.checkboxStates.length; i++) {
        this.checkboxStates[i].enabled = this.checkboxStates[i - 1].checked;
      }
    }
  }


  get isSinglePayment(): boolean {
    return this.paymentLineItems.length === 1;
  }

  get singlePayment(): PaymentLineItem | null {
    return this.isSinglePayment ? this.paymentLineItems[0] : null;
  }

  getTotalAmount(): number {
    return [...this.selectedIndices].reduce((acc, i) => {
      const amount = this.paymentLineItems[i]?.paymentDueAmount || 0;
      return acc + amount;
    }, 0);
  }


  onConfirm() {
    const hasEmail = this.emailId && this.emailId.trim() !== '';
    const hasContact = this.contactNumber && this.contactNumber.trim() !== '';

    const isValidEmail = !hasEmail || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(this.emailId.trim());
    const isValidContact = !hasContact || /^[689]\d{7}$/.test(this.contactNumber.trim());

    if (!hasEmail && !hasContact) {
      this.errorMessage = 'Either an Email ID or Contact is required.';
      return;
    }

    if (hasEmail && !isValidEmail) {
      this.errorMessage = 'Please enter a valid Email ID.';
      return;
    }

    if (hasContact && !isValidContact) {
      this.errorMessage = 'Please enter a valid contact number.';
      return;
    }
    const selectedPACs: string[] = [...this.selectedIndices].map(
      (i) => this.paymentLineItems[i].instalmentNumber
    );

    const pacAmountsMap: { [key: string]: number } = {};
    [...this.selectedIndices].forEach((i) => {
      const inst = this.paymentLineItems[i];
      pacAmountsMap[inst.instalmentNumber] = inst.paymentDueAmount || 0;
    });
    const terminalId = localStorage.getItem('kioskCode');
    const pacUpdateRequest: PacUpdateRequest = {
      emailId: this.emailId || '',
      contactNumber: this.contactNumber ? `65${this.contactNumber.trim()}` : '',
      pacNumber: this.pacNumber,
      selectedAmountPayable: this.isSinglePayment ? this.singlePayment?.paymentDueAmount : this.getTotalAmount(),
      paymentFrequency: PaymentFrequency.I,
      paymentCategory: PaymentCategory.COURT_PAYMENT,
      selectedPacsCount: selectedPACs.length,
      terminalId: terminalId || ''
    };
    const payload = {
      payerData: this.payerData,
      pacNumber: this.pacNumber,
      payerId: this.payerData.payerID,
      totalAmountPayable: this.isSinglePayment ? this.singlePayment?.paymentDueAmount : this.getTotalAmount(),
      balancePayable: this.isSinglePayment ? this.singlePayment?.paymentDueAmount : this.getTotalAmount(),
      amountPaid: 0,
      payerName: this.payerData.payerName,
      emailId: this.emailId,
      contactNumber: this.contactNumber,
      selectedPacNumbers: selectedPACs,
      pacAmountsMap: pacAmountsMap

    };
    this.pacService.updateAdditionalInfo(pacUpdateRequest).subscribe({
      next: (response: AdditionalPACData) => {
        this.router.navigate(['court-payment'], {state: { pacDetails: payload}});
      },
      error: (err) => {
      }
    });
  }

  onCancel(): void {
    this.location.back();
  }

  isConfirmDisabled(emailControl: NgModel, contactControl: NgModel): boolean {
    const emailValid = !!(this.emailId?.trim() && !emailControl.invalid);
    const contactValid = !!(this.contactNumber?.trim() && !contactControl.invalid);
    const hasAtLeastOneValid = emailValid || contactValid;
    const noInstallmentSelected = !this.isSinglePayment && this.selectedIndices.size === 0;
    return !hasAtLeastOneValid || noInstallmentSelected;
  }

  showAmountFormatted(value: number | null | undefined): string {
    return AppConstants.showAmountFormatted(value);
  }

}
